import { useEffect, useState, useCallback } from "react";
import { Plus, Library, Pencil, Trash2, Video, HelpCircle, FileText, Upload, Loader2, X, DownloadCloud } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loading, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const EMPTY = { name: "", category: "法定研修", purpose: "", description: "", pass_score: 70, status: "公開", videos: [], questions: [], materials: [] };

function UploadInput({ accept, onDone, label, testid }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/platform/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onDone(data.url, data.filename);
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  return (
    <label className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-slate-300 cursor-pointer hover:bg-slate-50" data-testid={testid}>
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {label}
      <input type="file" accept={accept} className="hidden" onChange={handle} disabled={busy} />
    </label>
  );
}

function MasterEditor({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial ? { ...EMPTY, ...initial } : EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  // videos
  const addVideo = () => set("videos", [...f.videos, { title: "", url: "", duration: 0, chapters: [] }]);
  const updVideo = (i, k, v) => set("videos", f.videos.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const delVideo = (i) => set("videos", f.videos.filter((_, j) => j !== i));
  const addChapter = (vi) => set("videos", f.videos.map((x, j) => j === vi ? { ...x, chapters: [...(x.chapters || []), { title: "", start_time: 0, end_time: 0 }] } : x));
  const updChapter = (vi, ci, k, v) => set("videos", f.videos.map((x, j) => j === vi ? { ...x, chapters: x.chapters.map((c, k2) => k2 === ci ? { ...c, [k]: v } : c) } : x));
  const delChapter = (vi, ci) => set("videos", f.videos.map((x, j) => j === vi ? { ...x, chapters: x.chapters.filter((_, k2) => k2 !== ci) } : x));

  // questions
  const addQ = () => set("questions", [...f.questions, { qtype: "single", text: "", options: [{ label: "", correct: true }, { label: "", correct: false }], answer_bool: true, explanation: "", points: 25 }]);
  const updQ = (i, k, v) => set("questions", f.questions.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const delQ = (i) => set("questions", f.questions.filter((_, j) => j !== i));
  const addOpt = (qi) => set("questions", f.questions.map((x, j) => j === qi ? { ...x, options: [...x.options, { label: "", correct: false }] } : x));
  const updOpt = (qi, oi, k, v) => set("questions", f.questions.map((x, j) => j === qi ? { ...x, options: x.options.map((o, k2) => k2 === oi ? { ...o, [k]: v } : o) } : x));
  const delOpt = (qi, oi) => set("questions", f.questions.map((x, j) => j === qi ? { ...x, options: x.options.filter((_, k2) => k2 !== oi) } : x));

  // materials
  const addMat = () => set("materials", [...f.materials, { title: "", url: "" }]);
  const updMat = (i, k, v) => set("materials", f.materials.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const delMat = (i) => set("materials", f.materials.filter((_, j) => j !== i));

  const save = async () => {
    if (!f.name) { toast.error("教材名は必須です"); return; }
    if (initial?.id && !window.confirm("この教材を使用中の全法人の研修へ変更を自動反映します。受講中・受講済み従業者の進捗（動画・テスト・所感・修了状態）はリセットされ、最初から受講し直しになります。よろしいですか？")) return;
    setBusy(true);
    const payload = {
      name: f.name, category: f.category, purpose: f.purpose, description: f.description,
      pass_score: Number(f.pass_score) || 70, status: f.status,
      videos: f.videos, questions: f.questions, materials: f.materials,
    };
    try {
      if (initial?.id) {
        const { data } = await api.put(`/platform/master-trainings/${initial.id}`, payload);
        toast.success(`保存しました（${data?.synced_trainings ?? 0}件の法人研修へ自動反映）`);
      } else {
        await api.post("/platform/master-trainings", payload);
        toast.success("研修コンテンツを保存しました");
      }
      onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
      <DialogHeader><DialogTitle>{initial?.id ? "研修コンテンツを編集" : "研修コンテンツを作成"}</DialogTitle></DialogHeader>
      <Tabs defaultValue="basic">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="basic" data-testid="me-tab-basic">基本情報</TabsTrigger>
          <TabsTrigger value="videos" data-testid="me-tab-videos">動画（{f.videos.length}）</TabsTrigger>
          <TabsTrigger value="questions" data-testid="me-tab-questions">設問（{f.questions.length}）</TabsTrigger>
          <TabsTrigger value="materials" data-testid="me-tab-materials">資料（{f.materials.length}）</TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="space-y-3 pt-2">
          <div><Label>教材名 *</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} className="mt-1" data-testid="me-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>カテゴリ</Label>
              <Select value={f.category || "法定研修"} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="mt-1" data-testid="me-category"><SelectValue placeholder="カテゴリを選択" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="法定研修">法定研修</SelectItem>
                  <SelectItem value="入社時研修">入社時研修</SelectItem>
                  <SelectItem value="専門研修">専門研修</SelectItem>
                  <SelectItem value="その他">その他</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>公開状態</Label>
              <Select value={f.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="mt-1" data-testid="me-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="公開">公開</SelectItem><SelectItem value="非公開">非公開</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>合格点（%）</Label><Input type="number" value={f.pass_score} onChange={(e) => set("pass_score", e.target.value)} className="mt-1 w-32" data-testid="me-passscore" /></div>
          <div><Label>研修目的</Label><Textarea value={f.purpose || ""} onChange={(e) => set("purpose", e.target.value)} className="mt-1" rows={2} data-testid="me-purpose" /></div>
          <div><Label>説明</Label><Textarea value={f.description || ""} onChange={(e) => set("description", e.target.value)} className="mt-1" rows={2} data-testid="me-description" /></div>
        </TabsContent>

        <TabsContent value="videos" className="space-y-4 pt-2">
          {f.videos.map((v, i) => (
            <div key={i} className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`me-video-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-500 flex items-center gap-1"><Video className="w-3.5 h-3.5" /> 動画 {i + 1}</span>
                <button onClick={() => delVideo(i)} className="text-rose-500"><Trash2 className="w-4 h-4" /></button>
              </div>
              <Input value={v.title} onChange={(e) => updVideo(i, "title", e.target.value)} placeholder="動画タイトル" data-testid={`me-video-title-${i}`} />
              <div className="flex items-center gap-2">
                <Input value={v.url || ""} onChange={(e) => updVideo(i, "url", e.target.value)} placeholder="動画URL（またはアップロード）" className="flex-1" />
                <UploadInput accept="video/*" label="アップロード" testid={`me-video-upload-${i}`} onDone={(url) => updVideo(i, "url", url)} />
              </div>
              <Input type="number" value={v.duration || 0} onChange={(e) => updVideo(i, "duration", Number(e.target.value))} placeholder="尺（秒）" className="w-40" />
              <div className="pl-3 border-l-2 border-slate-100 space-y-2">
                {(v.chapters || []).map((c, ci) => (
                  <div key={ci} className="flex items-center gap-2">
                    <Input value={c.title} onChange={(e) => updChapter(i, ci, "title", e.target.value)} placeholder="章タイトル" className="flex-1 h-8" />
                    <Input type="number" value={c.start_time} onChange={(e) => updChapter(i, ci, "start_time", Number(e.target.value))} placeholder="開始秒" className="w-20 h-8" />
                    <Input type="number" value={c.end_time} onChange={(e) => updChapter(i, ci, "end_time", Number(e.target.value))} placeholder="終了秒" className="w-20 h-8" />
                    <button onClick={() => delChapter(i, ci)} className="text-rose-400"><X className="w-4 h-4" /></button>
                  </div>
                ))}
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => addChapter(i)} data-testid={`me-add-chapter-${i}`}>+ チャプター追加</Button>
              </div>
            </div>
          ))}
          <Button variant="outline" onClick={addVideo} data-testid="me-add-video"><Plus className="w-4 h-4 mr-1" />動画を追加</Button>
        </TabsContent>

        <TabsContent value="questions" className="space-y-4 pt-2">
          {f.questions.map((q, i) => (
            <div key={i} className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`me-q-${i}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-slate-500 flex items-center gap-1"><HelpCircle className="w-3.5 h-3.5" /> 設問 {i + 1}</span>
                <div className="flex items-center gap-2">
                  <Select value={q.qtype} onValueChange={(v) => updQ(i, "qtype", v)}>
                    <SelectTrigger className="h-8 w-32" data-testid={`me-q-type-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">単一選択</SelectItem>
                      <SelectItem value="multiple">複数選択</SelectItem>
                      <SelectItem value="truefalse">○×</SelectItem>
                      <SelectItem value="text">記述</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input type="number" value={q.points} onChange={(e) => updQ(i, "points", Number(e.target.value))} className="h-8 w-20" placeholder="配点" />
                  <button onClick={() => delQ(i)} className="text-rose-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <Textarea value={q.text} onChange={(e) => updQ(i, "text", e.target.value)} placeholder="設問文" rows={2} data-testid={`me-q-text-${i}`} />
              {(q.qtype === "single" || q.qtype === "multiple") && (
                <div className="space-y-1.5">
                  {q.options.map((o, oi) => (
                    <div key={oi} className="flex items-center gap-2">
                      <input type="checkbox" checked={!!o.correct} onChange={(e) => updOpt(i, oi, "correct", e.target.checked)} data-testid={`me-q-${i}-opt-${oi}-correct`} />
                      <Input value={o.label} onChange={(e) => updOpt(i, oi, "label", e.target.value)} placeholder="選択肢" className="flex-1 h-8" />
                      <button onClick={() => delOpt(i, oi)} className="text-rose-400"><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => addOpt(i)}>+ 選択肢追加</Button>
                  <p className="text-[11px] text-slate-400">チェックが正解です</p>
                </div>
              )}
              {q.qtype === "truefalse" && (
                <Select value={String(q.answer_bool)} onValueChange={(v) => updQ(i, "answer_bool", v === "true")}>
                  <SelectTrigger className="h-8 w-40" data-testid={`me-q-bool-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="true">正解：○（正しい）</SelectItem><SelectItem value="false">正解：×（誤り）</SelectItem></SelectContent>
                </Select>
              )}
              <Input value={q.explanation || ""} onChange={(e) => updQ(i, "explanation", e.target.value)} placeholder="解説（任意）" className="h-8" />
            </div>
          ))}
          <Button variant="outline" onClick={addQ} data-testid="me-add-q"><Plus className="w-4 h-4 mr-1" />設問を追加</Button>
        </TabsContent>

        <TabsContent value="materials" className="space-y-3 pt-2">
          {f.materials.map((m, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`me-mat-${i}`}>
              <FileText className="w-4 h-4 text-slate-400 shrink-0" />
              <Input value={m.title} onChange={(e) => updMat(i, "title", e.target.value)} placeholder="資料名" className="h-8" />
              <Input value={m.url || ""} onChange={(e) => updMat(i, "url", e.target.value)} placeholder="URL" className="h-8 flex-1" />
              <UploadInput accept="*/*" label="UP" testid={`me-mat-upload-${i}`} onDone={(url, name) => { updMat(i, "url", url); if (!m.title) updMat(i, "title", name); }} />
              <button onClick={() => delMat(i)} className="text-rose-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <Button variant="outline" onClick={addMat} data-testid="me-add-mat"><Plus className="w-4 h-4 mr-1" />資料を追加</Button>
        </TabsContent>
      </Tabs>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>キャンセル</Button>
        <Button onClick={save} disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="me-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "保存"}</Button>
      </DialogFooter>
    </DialogContent>
  );
}

export default function PlatformMasterTrainings() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(undefined); // undefined=closed, null=new, obj=edit
  const [importing, setImporting] = useState(false);
  const load = useCallback(() => { api.get("/platform/master-trainings").then(({ data }) => setItems(data)); }, []);
  useEffect(() => { load(); }, [load]);

  const importFromTrainings = async () => {
    if (!window.confirm("全法人のテナント研修（動画あり）から、まだ登録されていない教材を取り込みます。よろしいですか？")) return;
    setImporting(true);
    try {
      const { data } = await api.post("/platform/master-trainings/import-from-trainings");
      toast.success(`取込完了：新規 ${data.created} 件 / 既存スキップ ${data.skipped} 件`);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setImporting(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`「${m.name}」を削除しますか？`)) return;
    try { await api.delete(`/platform/master-trainings/${m.id}`); toast.success("削除しました"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!items) return <Loading />;

  return (
    <div data-testid="platform-master">
      <div className="flex items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">研修コンテンツ（マスター教材）</h1>
          <p className="text-sm text-slate-400 mt-1">全法人が利用できる標準研修教材を集中管理</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" disabled={importing} onClick={importFromTrainings} data-testid="import-from-trainings-btn" className="border-slate-700 text-slate-200 hover:bg-slate-800 bg-transparent">
            {importing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <DownloadCloud className="w-4 h-4 mr-1" />}テナント研修から取り込み
          </Button>
          <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900" onClick={() => setEditing(null)} data-testid="create-master-btn"><Plus className="w-4 h-4 mr-1" />教材を作成</Button>
        </div>
      </div>

      {items.length ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((m) => (
            <div key={m.id} className="bg-slate-900 rounded-xl border border-slate-800 p-5 flex flex-col" data-testid={`master-card-${m.id}`}>
              <div className="flex items-start justify-between">
                <div className="w-9 h-9 rounded-lg bg-amber-500/15 text-amber-400 flex items-center justify-center"><Library className="w-5 h-5" /></div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${m.status === "公開" ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-700 text-slate-400"}`}>{m.status}</span>
              </div>
              <div className="mt-3 font-semibold text-slate-100">{m.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">{m.category}</div>
              <p className="text-xs text-slate-400 mt-2 line-clamp-2 flex-1">{m.purpose || m.description}</p>
              <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-3">
                <span>動画 {m.videos?.length || 0}</span><span>設問 {m.questions?.length || 0}</span><span>導入 {m.used_count || 0}法人</span>
              </div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" className="flex-1 border-slate-700 text-slate-200 hover:bg-slate-800" onClick={() => setEditing(m)} data-testid={`edit-master-${m.id}`}><Pencil className="w-4 h-4 mr-1" />編集</Button>
                <Button size="sm" variant="outline" className="border-slate-700 text-rose-400 hover:bg-slate-800" onClick={() => remove(m)} data-testid={`delete-master-${m.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      ) : <EmptyState title="研修コンテンツがありません" hint="「教材を作成」から追加してください" />}

      <Dialog open={editing !== undefined} onOpenChange={(o) => !o && setEditing(undefined)}>
        {editing !== undefined && <MasterEditor initial={editing} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />}
      </Dialog>
    </div>
  );
}
